import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  amazonOrdersApi,
  BULK_ACTIONS,
  ORDER_STATUSES,
  type ActivityEntry,
  type AIRecommendation,
  type DashboardSummary,
  type Order,
  type OrderGridRow,
  type OrderNotification,
  type OrderProfitability,
  type ReturnsRefundCenter,
  type SearchResult,
  type ShipmentCenterSummary,
} from '../../api/amazonorders';

const WORKSPACE = (getWorkspaceId() ?? '');

export function OrdersDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [rows, setRows] = useState<OrderGridRow[]>([]);
  const [total, setTotal] = useState(0);
  const [shipments, setShipments] = useState<ShipmentCenterSummary | null>(null);
  const [returns, setReturns] = useState<ReturnsRefundCenter | null>(null);
  const [notifications, setNotifications] = useState<OrderNotification[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [profit, setProfit] = useState<OrderProfitability | null>(null);
  const [recs, setRecs] = useState<AIRecommendation[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, grid, ship, ret, act, notifs] = await Promise.all([
        amazonOrdersApi.dashboard(WORKSPACE),
        amazonOrdersApi.ordersGrid(WORKSPACE),
        amazonOrdersApi.shipmentCenter(WORKSPACE),
        amazonOrdersApi.returnsCenter(WORKSPACE),
        amazonOrdersApi.listActivity(undefined, 20),
        amazonOrdersApi.listNotifications(true),
      ]);
      setDashboard(dash);
      setRows(grid.rows);
      setTotal(grid.total);
      setShipments(ship);
      setReturns(ret);
      setActivity(act);
      setNotifications(notifs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load orders');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runSearch = async () => {
    if (!searchText.trim()) {
      setSearchResult(null);
      return;
    }
    setBusy(true);
    try {
      const result = await amazonOrdersApi.globalSearch(searchText, WORKSPACE);
      setSearchResult(result);
    } finally {
      setBusy(false);
    }
  };

  const loadOrder = async (id: string) => {
    setSelectedId(id);
    try {
      const [o, p, recommendations] = await Promise.all([
        amazonOrdersApi.getOrder(id),
        amazonOrdersApi.profitability(id),
        amazonOrdersApi.recommendations(id),
      ]);
      setOrder(o);
      setProfit(p);
      setRecs(recommendations);
    } catch {
      setOrder(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: string) => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      await amazonOrdersApi.triggerBulk({
        action,
        order_ids: [...selectedIds],
        workspace_id: WORKSPACE,
        parameters: action === 'update_status' ? { status: 'shipped' } : undefined,
      });
      setSelectedIds(new Set());
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const displayRows: OrderGridRow[] = searchResult?.orders?.length
    ? searchResult.orders.map((o) => ({
        id: o.id,
        amazon_order_id: o.identifiers.amazon_order_id,
        marketplace_id: o.marketplace_id,
        status: o.status,
        channel: o.fulfillment?.channel ?? 'FBA',
        line_count: o.line_items?.length ?? 0,
        item_total: o.financials?.item_total ?? 0,
        currency: o.financials?.currency,
        purchase_date: o.purchase_date,
        has_open_return: false,
        shipment_count: o.fulfillment?.shipments?.length ?? 0,
        updated_at: o.updated_at,
      }))
    : rows.filter((r) => !statusFilter || r.status === statusFilter);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Order Operations & Fulfillment</h1>
          <p className="mt-1 text-sm text-slate-600">
            Enterprise order management, shipment tracking, returns, profitability, and AI intelligence
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
        >
          Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {dashboard && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { label: 'Orders', value: dashboard.total_orders },
            { label: 'Pending Ship', value: dashboard.pending_shipment },
            { label: 'Shipped Today', value: dashboard.shipped_today },
            { label: 'Returns', value: dashboard.return_count },
            { label: 'Revenue', value: `$${dashboard.total_revenue.toFixed(0)}` },
            { label: 'Notifications', value: dashboard.unread_notifications },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase text-slate-400">{c.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {shipments && (
          <section className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
            <h2 className="font-semibold text-blue-900">Shipment Center</h2>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-blue-800">
              <span>Total: {shipments.total_shipments}</span>
              <span>In transit: {shipments.in_transit}</span>
              <span>Delivered: {shipments.delivered}</span>
              <span>Delayed: {shipments.delayed}</span>
            </div>
          </section>
        )}
        {returns && (
          <section className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
            <h2 className="font-semibold text-violet-900">Returns & Refund Center</h2>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-violet-800">
              <span>Open: {returns.open_returns}</span>
              <span>Completed: {returns.completed_returns}</span>
              <span>Pending refunds: {returns.pending_refunds}</span>
              <span>Refund value: ${returns.total_refund_value.toFixed(2)}</span>
            </div>
          </section>
        )}
      </div>

      {notifications.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-semibold">Notifications ({notifications.length})</h2>
          <ul className="mt-2 space-y-1 text-sm text-slate-600">
            {notifications.slice(0, 5).map((n) => (
              <li key={n.id}>
                <strong>{n.title}</strong> — {n.body}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Global search (Amazon Order ID, SKU, ASIN…)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <button
          type="button"
          disabled={busy}
          onClick={runSearch}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Search
        </button>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
        {BULK_ACTIONS.map((action) => (
          <button
            key={action}
            type="button"
            disabled={busy || selectedIds.size === 0}
            onClick={() => runBulk(action)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium capitalize disabled:opacity-40"
          >
            Bulk {action.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 flex justify-between">
              <h2 className="font-semibold">Enterprise Orders Grid</h2>
              <span className="text-sm text-slate-500">{searchResult ? searchResult.total : total} orders</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2">Amazon Order ID</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Channel</th>
                    <th className="px-3 py-2">Total</th>
                    <th className="px-3 py-2">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayRows.map((row) => (
                    <tr
                      key={row.id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedId === row.id ? 'bg-brand-50' : ''}`}
                      onClick={() => loadOrder(row.id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.amazon_order_id}</td>
                      <td className="px-3 py-2 capitalize">{row.status.replace('_', ' ')}</td>
                      <td className="px-3 py-2">{row.channel}</td>
                      <td className="px-3 py-2">
                        {row.currency ?? '₹'}
                        {row.item_total.toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        {row.profit_margin != null ? `${Math.round(row.profit_margin * 100)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                  {displayRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No orders. Create orders via API or import.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {searchResult?.took_ms != null && (
              <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                Search took {searchResult.took_ms}ms
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          {order ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <h2 className="font-semibold">Order Details</h2>
              <p className="font-mono text-sm">{order.identifiers.amazon_order_id}</p>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">Status</dt>
                <dd className="capitalize">{order.status.replace('_', ' ')}</dd>
                <dt className="text-slate-500">Lines</dt>
                <dd>{order.line_items?.length ?? 0}</dd>
                <dt className="text-slate-500">Channel</dt>
                <dd>{order.fulfillment?.channel ?? '—'}</dd>
                <dt className="text-slate-500">Country</dt>
                <dd>{order.shipping?.country ?? '—'}</dd>
              </dl>
              {profit && (
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="font-medium">Profitability</p>
                  <p>Revenue: ${profit.revenue.toFixed(2)} · Net: ${profit.net_profit.toFixed(2)}</p>
                  <p>Margin: {Math.round(profit.margin_percent * 100)}%</p>
                </div>
              )}
              {recs.length > 0 && (
                <div>
                  <p className="text-sm font-medium">AI Order Intelligence</p>
                  <ul className="mt-1 space-y-1 text-xs text-slate-600">
                    {recs.map((r) => (
                      <li key={r.id}>{r.title}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              Select an order to view details, profitability, and AI insights
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold">Timeline</h2>
            <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto text-xs text-slate-600">
              {activity.map((a) => (
                <li key={a.id}>
                  <span className="text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  <br />
                  {a.message}
                </li>
              ))}
              {activity.length === 0 && <li>No recent activity</li>}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
